import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import comicActions from 'src/redux/actions/comicActions';
import CustomTable from 'src/components/CustomTable';
import { DeleteOutlined, EditOutlined, FileAddOutlined } from '@ant-design/icons';
import comicSelectors from 'src/redux/selectors/comicSelectors';
import CustomTags from 'src/components/CustomTags';
import { valueFilter, greaterFilter, emptyFilter, alphabetSort } from 'src/utils/helpers';
import ModalAddComic from './ModalAddComic';
import ModalAddChapter from './ModalAddChapter';
import ModalChapters from './ModalChapters';
import './index.css';
import genreActions from 'src/redux/actions/genreActions';
import { Popconfirm } from 'antd';
import CustomDate from 'src/components/CustomDate';
import RatingIndicator from 'src/components/RatingIndicator';

const Comics = () => {
  const dispatch = useDispatch();
  const loading = useSelector(comicSelectors.loading);
  const comics = useSelector(comicSelectors.comics);
  const [modalAddComicVisible, setModalAddComicVisible] = useState(false);
  const [modalAddChapterVisible, setModalAddChapterVisible] = useState(false);
  const [modalChaptersVisible, setModalChaptersVisible] = useState(false);
  const [editingComic, setEditingComic] = useState(null);
  const [comic, setComic] = useState(null);

  const fetchGenres = () => {
    dispatch(genreActions.fetchGenres());
  };

  const fetchComics = () => {
    dispatch(comicActions.fetchComics());
  };

  const deleteComics = (selectedIds) => {
    dispatch(comicActions.deleteComics(selectedIds, () => fetchComics()));
  };

  useEffect(() => {
    fetchComics();
    fetchGenres();
  }, []);

  const columns = [
    {
      title: 'Cover',
      dataIndex: 'cover',
      width: 100,
      align: 'center',
      render: (value) => <img alt="comic's cover" src={value} style={{ height: 100, aspectRatio: 2 / 3, objectFit: 'contain' }} />,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      width: 200,
      align: 'center',
      searchable: true,
    },
    {
      title: 'Chapter Count',
      dataIndex: 'chapterCount',
      width: 200,
      align: 'center',
      render: (value, record) => <a style={{ display: 'flex', flex: 1, height: 100, alignItems: 'center', justifyContent: 'center' }} onClick={record.chapterCount > 0 ? () => {
        setComic(record);
        setModalChaptersVisible(true);
      } : null}>{value}</a>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 200,
      align: 'center',
      filters: [
        {
          text: 'Updating',
          value: 'updating',
        },
        {
          text: 'Completed',
          value: 'completed',
        },
      ],
      onFilter: (value, record) => valueFilter(value, record, 'status'),
    },
    {
      title: 'Views',
      dataIndex: 'views',
      width: 200,
      align: 'center',
      filters: [
        {
          text: '≥ 500',
          value: 500,
        },
        {
          text: '≥ 1000',
          value: 1000,
        },
        {
          text: '≥ 2000',
          value: 2000,
        },
        {
          text: '≥ 5000',
          value: 5000,
        },
        {
          text: '≥ 10000',
          value: 10000,
        },
      ],
      onFilter: (value, record) => greaterFilter(value, record, 'views'),
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      width: 200,
      align: 'center',
      render: (value) => <RatingIndicator value={value} />,
      filters: [
        {
          text: <RatingIndicator value={1} />,
          value: 1,
        },
        {
          text: <RatingIndicator value={2} />,
          value: 2,
        },
        {
          text: <RatingIndicator value={3} />,
          value: 3,
        },
        {
          text: <RatingIndicator value={4} />,
          value: 4,
        },
        {
          text: <RatingIndicator value={5} />,
          value: 5,
        },
      ],
      onFilter: (value, record) => valueFilter(value, record, 'rating'),
    },
    {
      title: 'Genres',
      dataIndex: 'genres',
      width: 200,
      align: 'center',
      render: (values) => <CustomTags values={values} />,
    },
    {
      title: 'Author',
      dataIndex: 'author',
      width: 200,
      align: 'center',
      searchable: true,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: 500,
      searchable: true,
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      width: 200,
      align: 'center',
      // searchable: true,
      render: (value) => <CustomDate date={value} />
    },
    {
      title: 'Created by',
      dataIndex: 'createdBy',
      width: 200,
      align: 'center',
      searchable: true,
    },
    {
      title: 'Updated at',
      dataIndex: 'updatedAt',
      width: 200,
      align: 'center',
      // searchable: true,
      render: (value) => <CustomDate date={value} />
    },
    {
      title: 'Updated by',
      dataIndex: 'updatedBy',
      width: 200,
      align: 'center',
      searchable: true,
    },
    {
      title: '',
      key: 'actions',
      width: 70,
      fixed: 'right',
      render: (value, record) =>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Popconfirm
            title="Are you sure to delete this comic?"
            onConfirm={() => deleteComics([record.id])}
            okText="Yes"
            cancelText="No"
          >
            <DeleteOutlined className="action-button" style={{ ...styles.actionButton, backgroundColor: 'red' }} />
          </Popconfirm>
          <EditOutlined className="action-button" style={{ ...styles.actionButton, backgroundColor: 'gold' }} onClick={() => setEditingComic(record)} />
          <FileAddOutlined className="action-button" style={{ ...styles.actionButton, backgroundColor: '#28a745' }} onClick={() => {
            setComic(record);
            setModalAddChapterVisible(true);
          }} />
        </div>
      ,
    }
  ];

  return (
    <div>
      <CustomTable columns={columns} loading={loading} data={comics} onRefresh={fetchComics} onAdd={() => setModalAddComicVisible(true)} onDelete={deleteComics} />
      <ModalAddComic
        visible={modalAddComicVisible || editingComic}
        onCancel={() => {
          setModalAddComicVisible(false);
          setEditingComic(null);
        }}
        editingComic={editingComic}
      />
      <ModalAddChapter visible={modalAddChapterVisible} onCancel={() => setModalAddChapterVisible(false)} comic={comic} />
      <ModalChapters visible={modalChaptersVisible} onCancel={() => setModalChaptersVisible(false)} comic={comic} />
    </div>
  )
}

const styles = {
  actionButton: {
    color: 'white',
    padding: 5,
    marginTop: 5,
    marginBottom: 5,
    borderRadius: 5,
    cursor: 'pointer'
  }
};

export default Comics;